import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { Region } from '../../user-dashboard/shared/region.model';
import { RegionService } from '../../user-dashboard/shared/region.service';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { LoaderService } from '../services/loader.service';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-auto-page-heading',
  templateUrl: './auto-page-heading.component.html',
  styleUrls: ['./auto-page-heading.component.scss']
})
export class AutoPageHeadingComponent implements OnInit {

  @Input() public isCarrier: boolean;
  @Input() public selectedRegion: Region;
  @Input() public selectedCarrier: Carrier;
  @Input() public localStorageKeyPrefix: string;
  @Input() public title: string;
  @Input() public selectCarrierPlaceholder: string;
  @Input() public clearable = true;
  @Input() public subheadingIcon: string;
  @Input() public needVendorNumber = false;
  @Input() public lightData = false;

  @Output() public selectedRegionChange = new EventEmitter<Region>();
  @Output() public selectedCarrierChange = new EventEmitter<Carrier>();
  @Output() public onRegionFetchError = new EventEmitter<HttpErrorResponse>();
  @Output() public onCarrierFetchError = new EventEmitter<HttpErrorResponse>();

  public carriers: Carrier[] = [];
  public regions: Region[] = [];

  private queryParamRegionId: string = null;
  private queryParamCarrierId: string = null;

  constructor(
    private route: ActivatedRoute,
    private regionService: RegionService,
    private carrierService: CarrierService,
    private loaderService: LoaderService
  ) {}

  public ngOnInit(): void {
    if (this.isCarrier) {
      this.initAsCarrier();
    } else {
      this.initAsCHEP();
    }
  }

  public onSelectedRegionChange(selectedRegion: Region): void {
    const selectedRegionId = selectedRegion == null ? null : selectedRegion.id;
    this.storeSelectedRegion(selectedRegionId);

    this.selectedCarrier = null;
    this.fetchCarriersForSelectedRegion();
    this.selectedRegionChange.emit(selectedRegion);
  }

  public onSelectedCarrierChange(selectedCarrier: Carrier): void {
    const selectedCarrierId = selectedCarrier == null ? null : selectedCarrier.id;
    this.storeSelectedCarrier(selectedCarrierId);
    if (!this.selectedRegion && selectedCarrier) {
      this.selectedRegion = this.selectedCarrier.region;
      this.selectedRegionChange.emit(this.selectedCarrier.region);
    }
    this.selectedCarrierChange.emit(selectedCarrier);
  }

  private initAsCarrier(): void {
    this.loaderService.startLoad();
    this.carrierService.getSelfCarrier().pipe(
      tap(carrier => {
        this.selectedRegionChange.emit(carrier.region);
        this.selectedCarrierChange.emit(carrier);
      }),
      catchError((e: HttpErrorResponse) => {
        this.onCarrierFetchError.emit(e);
        return EMPTY;
      }),
      finalize(() => this.loaderService.stopLoad())
    ).subscribe()
  }

  private initAsCHEP(): void {
    this.loaderService.startLoad();
    this.route.queryParams.subscribe(queryParams => {
      if (this.queryParamRegionId !== queryParams.regionId) {
        this.queryParamRegionId = queryParams.regionId;
        this.queryParamCarrierId = queryParams.carrierId;
        this.getRegionsFetchObservable()
          .pipe(
            tap(() => this.initSelectedRegion()),
            switchMap(() => this.getCarriersFetchObservable()),
            tap(() => this.initSelectedCarrier()),
            finalize(() => this.loaderService.stopLoad())
          )
          .subscribe();
        return;
      }
      if (this.queryParamCarrierId !== queryParams.carrierId) {
        this.queryParamCarrierId = queryParams.carrierId;
        this.getCarriersFetchObservable()
          .pipe(
            tap(() => this.initSelectedCarrier()),
            finalize(() => this.loaderService.stopLoad())
          )
          .subscribe();
      }
    });
  }

  private initSelectedRegion(): void {
    const availableRegionsIds = this.regions.map(region => region.id);
    const locallyStoredRegionId = this.getSelectedRegionInStorage();
    let selectedRegionId: string;

    if (this.selectedRegion != null && availableRegionsIds.includes(this.selectedRegion.id)) {
      selectedRegionId = this.selectedRegion.id;
    } else if (locallyStoredRegionId != null && availableRegionsIds.includes(locallyStoredRegionId)) {
      selectedRegionId = locallyStoredRegionId;
    } else if (this.queryParamRegionId && availableRegionsIds.includes(this.queryParamRegionId)) {
      selectedRegionId = this.queryParamRegionId;
    }

    if (selectedRegionId != null && selectedRegionId !== this.selectedRegion?.id) {
      this.selectedRegion = this.regions.find(region => region.id === selectedRegionId);
      this.selectedRegionChange.emit(this.selectedRegion);
    }
  }

  private initSelectedCarrier(): void {
    const availableCarriersIds = this.carriers.map(carrier => carrier.id);
    const locallyStoredCarrierId = this.getSelectedCarrierInStorage();
    let selectedCarrierId: string;

    if (this.selectedCarrier != null && availableCarriersIds.includes(this.selectedCarrier.id)) {
      selectedCarrierId = this.selectedCarrier.id;
    } else if (locallyStoredCarrierId != null && availableCarriersIds.includes(locallyStoredCarrierId)) {
      selectedCarrierId = locallyStoredCarrierId
    } else if (!this.clearable && availableCarriersIds.length >= 1) {
      selectedCarrierId = availableCarriersIds[0];
    } else if (this.queryParamCarrierId != null && availableCarriersIds.includes(this.queryParamCarrierId)) {
      selectedCarrierId = this.queryParamCarrierId;
    }

    if (selectedCarrierId && selectedCarrierId !== this.selectedCarrier?.id) {
      this.selectedCarrier = this.carriers.find(carrier => carrier.id === selectedCarrierId)
      this.selectedCarrierChange.emit(this.selectedCarrier);
    }
  }

  private fetchCarriersForSelectedRegion(): void {
    this.loaderService.startLoad();
    this.getCarriersFetchObservable()
      .pipe(finalize(() => this.loaderService.stopLoad()))
      .subscribe()
  }

  private getRegionsFetchObservable(): Observable<Region[]> {
    return this.regionService.getOwnRegionsLight()
      .pipe(
        tap(regions => this.regions = regions),
        catchError((e: HttpErrorResponse) => {
          this.onRegionFetchError.emit(e);
          return EMPTY;
        })
      )
  }

  private getCarriersFetchObservable(): Observable<Carrier[]> {
    return (
      this.selectedRegion == null
        ? of([])
        : this.getCarriersFetchObservableOfMode()
    ).pipe(
      tap((carriers: Carrier[]) => this.carriers = carriers
        .filter(carrier => this.needVendorNumber ? carrier.vendorNumbers !== null && carrier.vendorNumbers.length > 0 : true)
      ),
      catchError((e: HttpErrorResponse) => {
        this.onCarrierFetchError.emit(e);
        return EMPTY;
      })
    );
  }

  private getCarriersFetchObservableOfMode(): Observable<Carrier[]> {
    return this.lightData
      ? this.carrierService.getAllLightByRegions([this.selectedRegion.id])
      : this.carrierService.searchByRegion(this.selectedRegion.id);
  }

  private storeSelectedRegion(selectedRegionId?: string): void {
    if (selectedRegionId != null) {
      localStorage.removeItem(this.getRegionStorageKey())
    } else {
      localStorage.setItem(this.getRegionStorageKey(), selectedRegionId);
    }
  }

  private getSelectedRegionInStorage(): string {
    return localStorage.getItem(this.getRegionStorageKey());
  }

  private storeSelectedCarrier(selectedCarrierId?: string): void {
    if (selectedCarrierId != null) {
      localStorage.removeItem(this.getCarrierStorageKey())
    } else {
      localStorage.setItem(this.getCarrierStorageKey(), selectedCarrierId);
    }
  }

  private getSelectedCarrierInStorage(): string {
    return localStorage.getItem(this.getCarrierStorageKey());
  }

  private getRegionStorageKey(): string {
    return `${this.localStorageKeyPrefix}.region`;
  }

  private getCarrierStorageKey(): string {
    return `${this.localStorageKeyPrefix}.carrier`;
  }
}
